<template>
  <div class="reschedule-preview pa-4">
    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="64" />
    </v-overlay>

    <!-- 標題與 Tabs -->
    <div class="preview-header mb-4">
      <h3 class="text-subtitle-1 font-weight-bold mb-3">預覽</h3>
      <v-tabs v-model="activeTab" background-color="transparent" height="36">
        <v-tab class="text-body-2">操作畫面</v-tab>
        <v-tab class="text-body-2">送出後</v-tab>
        <v-tab class="text-body-2">LINE卡片</v-tab>
      </v-tabs>
    </div>

    <!-- 預覽內容區 -->
    <v-tabs-items v-model="activeTab" class="preview-content-area">
      <v-tab-item>
        <div class="d-flex justify-center align-start">
          <patient-operation-preview
            :mock-data="mockData"
            :reasons="displayReasons"
            :contacts="displayContacts"
            :selected-reason.sync="previewReason"
            :selected-contact.sync="previewContact"
          />
        </div>
      </v-tab-item>

      <v-tab-item>
        <div class="d-flex justify-center align-start">
          <submitted-preview
            :mock-data="mockData"
            :selected-reason="selectedReason"
            :selected-contact="selectedContact"
          />
        </div>
      </v-tab-item>

      <v-tab-item>
        <div class="d-flex justify-center align-start">
          <line-card-preview
            :mock-data="mockData"
            :selected-reason="selectedReason"
            :selected-contact="selectedContact"
          />
        </div>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getRescheduleOptionsAPI } from '@/api'
import LineCardPreview from './preview/LineCardPreview.vue'
import PatientOperationPreview from './preview/PatientOperationPreview.vue'
import SubmittedPreview from './preview/SubmittedPreview.vue'

export default {
  name: 'ReschedulePreview',

  components: {
    LineCardPreview,
    PatientOperationPreview,
    SubmittedPreview
  },

  props: {
    // 從父元件傳入的改約原因選項
    reasons: {
      type: Array,
      default: null
    },
    // 從父元件傳入的聯繫方式選項
    contacts: {
      type: Array,
      default: null
    }
  },

  data() {
    return {
      loading: false,
      activeTab: 0, // 預設顯示「操作畫面」
      fetchedReasons: [],
      fetchedContacts: [],
      previewReason: '',
      previewContact: '',
      // 預覽用假資料（集中管理）
      mockData: {
        clinicName: '丹科牙醫診所',
        clinicAddress: '台北市大安區幸福路88號2樓',
        patientName: '丹好牙',
        doctorName: '丹小科',
        appointmentDate: '2026/01/20',
        appointmentTime: '14:30',
        appointmentWeekday: '一',
        clickTime: '2026/01/19 10:25',
        clinicNote: '根管治療 FU'
      }
    }
  },

  computed: {
    ...mapGetters(['axiosParamData']),

    // 判斷是否使用 props 傳入的資料
    hasPropsReasons() {
      return this.reasons !== null && Array.isArray(this.reasons)
    },

    hasPropsContacts() {
      return this.contacts !== null && Array.isArray(this.contacts)
    },

    // 正規化 props 資料（支援字串或物件格式）
    normalizedPropsReasons() {
      if (!this.hasPropsReasons) return []
      return this.reasons.map((item, index) => {
        if (typeof item === 'string') {
          return { id: `prop-reason-${index}`, text: item }
        }
        return { id: item.id || `prop-reason-${index}`, text: item.text || item }
      })
    },

    normalizedPropsContacts() {
      if (!this.hasPropsContacts) return []
      return this.contacts.map((item, index) => {
        if (typeof item === 'string') {
          return { id: `prop-contact-${index}`, text: item }
        }
        return { id: item.id || `prop-contact-${index}`, text: item.text || item }
      })
    },

    // 實際顯示的資料（優先使用 props，否則使用 API 取得的資料）
    displayReasons() {
      if (this.hasPropsReasons && this.normalizedPropsReasons.length > 0) {
        return this.normalizedPropsReasons
      }
      return this.fetchedReasons
    },

    displayContacts() {
      if (this.hasPropsContacts && this.normalizedPropsContacts.length > 0) {
        return this.normalizedPropsContacts
      }
      return this.fetchedContacts
    },

    selectedReason() {
      // 如果 previewReason 存在於當前選項中，使用它；否則使用第一個選項
      const exists = this.displayReasons.some(r => r.text === this.previewReason)
      if (exists && this.previewReason) {
        return this.previewReason
      }
      return this.displayReasons[0]?.text || ''
    },

    selectedContact() {
      // 如果 previewContact 存在於當前選項中，使用它；否則使用第一個選項
      const exists = this.displayContacts.some(c => c.text === this.previewContact)
      if (exists && this.previewContact) {
        return this.previewContact
      }
      return this.displayContacts[0]?.text || ''
    }
  },

  watch: {
    // 當 props 變化時，重設預覽選擇
    reasons: {
      handler() {
        this.resetPreviewSelections()
      },
      deep: true
    },
    contacts: {
      handler() {
        this.resetPreviewSelections()
      },
      deep: true
    }
  },

  mounted() {
    // 只有在沒有傳入 props 時才從 API 取得資料
    if (!this.hasPropsReasons || !this.hasPropsContacts) {
      this.fetchOptions()
    } else {
      this.resetPreviewSelections()
    }
  },

  methods: {
    // 將 API 回應轉換為內部格式（支援物件或文字陣列）
    apiResponseToOptions(items) {
      if (!items || !Array.isArray(items) || items.length === 0) {
        return []
      }
      // 物件格式：[{ id: "123", text: "..." }]
      if (typeof items[0] === 'object' && items[0].text) {
        return items.map((item, index) => ({
          id: item.id || `option-${index}`,
          text: item.text
        }))
      }
      // 文字格式（相容）：["text1", "text2"]
      return items.map((text, index) => ({
        id: `option-${index}`,
        text
      }))
    },

    resetPreviewSelections() {
      this.previewReason = this.displayReasons[0]?.text || ''
      this.previewContact = this.displayContacts[0]?.text || ''
    },

    async fetchOptions() {
      this.loading = true
      try {
        const response = await getRescheduleOptionsAPI(this.axiosParamData)
        const data = response.data
        const systemDefaults = data.systemDefaults || {}

        // 處理改約原因（只有在沒有 props 時才設定）：診所自訂 > 系統預設
        if (!this.hasPropsReasons) {
          const reasons = this.apiResponseToOptions(data.reasons)
          if (reasons.length > 0) {
            this.fetchedReasons = reasons
          } else {
            this.fetchedReasons = this.apiResponseToOptions(systemDefaults.reasons)
          }
        }

        // 處理是否聯繫（只有在沒有 props 時才設定）：診所自訂 > 系統預設
        if (!this.hasPropsContacts) {
          const contacts = this.apiResponseToOptions(data.contacts)
          if (contacts.length > 0) {
            this.fetchedContacts = contacts
          } else {
            this.fetchedContacts = this.apiResponseToOptions(systemDefaults.contacts)
          }
        }

        // 設定預設選擇
        this.resetPreviewSelections()
      } catch (error) {
        console.error('Failed to fetch reschedule options:', error)
        // API 失敗時顯示空陣列
        if (!this.hasPropsReasons) {
          this.fetchedReasons = []
        }
        if (!this.hasPropsContacts) {
          this.fetchedContacts = []
        }
        this.resetPreviewSelections()
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.reschedule-preview {
  position: relative;
}

.preview-header {
  .v-tabs {
    .v-tab {
      min-width: auto;
      padding: 0 16px;
      text-transform: none;
      letter-spacing: normal;
    }
  }
}

.preview-content-area {
  background: transparent;

  .v-window-item {
    padding: 16px 0;
  }
}

</style>
